import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSearch, useTheme } from '@/hooks';
import { useDeviceType } from '@/utils/device';
import { Sidebar, DesktopHeader, DesktopGameGrid } from '@/components/desktop';
import { SearchBox, LoadingSpinner, ErrorState, MobileFooter } from '@/components/common';
import { ChevronLeft, Sun, Moon } from 'lucide-react';
import { MobileGameGrid } from '@/components/mobile';
import { useSiteSettings } from '@/contexts/settings-context';
import styles from './search.module.css';

export const SearchPage: React.FC = () => {
  const deviceType = useDeviceType();
  const isMobile = deviceType === 'mobile';
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { features } = useSiteSettings();
  const [urlParams] = useSearchParams();

  // 从 URL 参数读取初始查询
  const initialQuery = urlParams.get('q') || '';
  const [executedQuery, setExecutedQuery] = useState(initialQuery);

  const {
    data: results,
    isLoading,
    error,
    refetch,
  } = useSearch(executedQuery);

  // URL 参数变化时自动搜索
  useEffect(() => {
    const q = urlParams.get('q') || '';
    if (q && q !== executedQuery) {
      setExecutedQuery(q);
    }
  }, [urlParams]);

  const handleSearch = useCallback((q: string) => {
    setExecutedQuery(q);
  }, []);

  const handleRetry = () => {
    if (executedQuery) {
      refetch();
    }
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <div className={styles.mobileContainer}>
        <header className={styles.mobileHeader}>
          <div className={styles.headerRow}>
            <button className={styles.backBtn} onClick={() => navigate(-1)} title="返回">
              <ChevronLeft size={22} />
            </button>
            <div className={styles.pageTitle}>搜索</div>
            {features.darkMode && (
              <button className={styles.themeToggle} onClick={toggleTheme} title={theme === 'dark' ? '切换亮色模式' : '切换暗色模式'}>
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            )}
          </div>
          <SearchBox
            initialValue={initialQuery}
            onSearch={handleSearch}
            placeholder="搜索游戏..."
            showSuggestions={false}
          />
        </header>
        
        <main className={styles.mobileMain}>
          {executedQuery ? (
            isLoading ? (
              <LoadingSpinner text="搜索中..." />
            ) : error ? (
              <ErrorState 
                title="搜索失败" 
                message="无法完成搜索，请重试"
                onRetry={handleRetry}
              />
            ) : (
              <MobileGameGrid 
                games={results || []}
                title={`"${executedQuery}" 的搜索结果`}
                emptyText="未找到相关游戏"
              />
            )
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🔍</div>
              <p>输入关键词开始搜索</p>
            </div>
          )}
        </main>
        <MobileFooter />
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className={styles.desktopContainer}>
      <Sidebar activePath="/search" />
      
      <div className={styles.desktopContent}>
        <DesktopHeader showSearch={false} />
        
        <main className={styles.desktopMain}>
          <div className={styles.searchHeader}>
            <h1 className={styles.pageTitle}>搜索游戏</h1>
            <div className={styles.searchWrapper}>
              <SearchBox 
                initialValue={initialQuery}
                onSearch={handleSearch}
                placeholder="输入游戏名称、类型或平台..."
                showSuggestions={false}
              />
            </div>
          </div>
          
          {executedQuery ? (
            isLoading ? (
              <LoadingSpinner text="搜索中..." />
            ) : error ? (
              <ErrorState 
                title="搜索失败" 
                message="无法完成搜索，请重试"
                onRetry={handleRetry}
              />
            ) : (
              <DesktopGameGrid 
                games={results || []}
                title={`"${executedQuery}" 的搜索结果`}
                isLoading={isLoading}
                emptyText="未找到相关游戏，试试其他关键词"
              />
            )
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🔍</div>
              <h3>开始搜索</h3>
              <p>输入游戏名称、类型或平台查找你想玩的游戏</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SearchPage;
